import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppGroupButton from './components/WhatsAppGroupButton';
import InstallPrompt from './components/InstallPrompt';
import Home from './pages/Home';

const ListingDetail = lazy(() => import('./pages/ListingDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const RegisterUser = lazy(() => import('./pages/RegisterUser'));
const RegisterAgency = lazy(() => import('./pages/RegisterAgency'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CreateListing = lazy(() => import('./pages/CreateListing'));
const EditListing = lazy(() => import('./pages/EditListing'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const GoogleAccountChoice = lazy(() => import('./pages/GoogleAccountChoice'));
const Agences = lazy(() => import('./pages/Agences'));
const AgencePage = lazy(() => import('./pages/AgencePage'));
const ProfilPage = lazy(() => import('./pages/ProfilPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Besoins = lazy(() => import('./pages/Besoins'));
const BesoinDetail = lazy(() => import('./pages/BesoinDetail'));
const BesoinForm = lazy(() => import('./pages/BesoinForm'));
const MesBesoins = lazy(() => import('./pages/MesBesoins'));
const AdminBesoins = lazy(() => import('./pages/AdminBesoins'));
const AdminWhatsApp = lazy(() => import('./pages/AdminWhatsApp'));
const Diaspora = lazy(() => import('./pages/Diaspora'));
const SeoLandingPage = lazy(() => import('./pages/SeoLandingPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const AgenceGate = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'agency' && !user?.agence?.valide) {
    return <Navigate to="/tableau-de-bord" />;
  }
  return children;
};

const PrivateRoute = ({ children, roles }) => {
  const { user, chargement } = useAuth();
  const location = useLocation();
  if (chargement) return <div className="spinner-wrap"><div className="spinner"></div></div>;
  if (!user) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/connexion?redirect=${redirect}`} replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Navbar />
      <main>
        <Suspense fallback={<div className="spinner-wrap"><div className="spinner"></div></div>}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/annonce/:id" element={<ListingDetail />} />
          <Route path="/besoins" element={<Besoins />} />
          <Route path="/agences" element={<Agences />} />
          <Route path="/diaspora" element={<Diaspora />} />
          <Route path="/guide/:slug" element={<SeoLandingPage />} />
          <Route path="/besoin/:id" element={<BesoinDetail />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/inscription/particulier" element={<RegisterUser />} />
          <Route path="/inscription/agence" element={<RegisterAgency />} />
          <Route path="/tableau-de-bord" element={
            <PrivateRoute roles={['user', 'agency', 'admin']}>
              <Dashboard />
            </PrivateRoute>
          } />
          <Route path="/nouvelle-annonce" element={
            <PrivateRoute roles={['user', 'agency', 'admin']}>
              <AgenceGate><CreateListing /></AgenceGate>
            </PrivateRoute>
          } />
          <Route path="/modifier-annonce/:id" element={
            <PrivateRoute roles={['user', 'agency', 'admin']}>
              <EditListing />
            </PrivateRoute>
          } />
          <Route path="/publier-besoin" element={
            <PrivateRoute roles={['user', 'agency', 'admin']}>
              <BesoinForm />
            </PrivateRoute>
          } />
          <Route path="/modifier-besoin/:id" element={
            <PrivateRoute roles={['user', 'agency', 'admin']}>
              <BesoinForm />
            </PrivateRoute>
          } />
          <Route path="/mes-besoins" element={
            <PrivateRoute roles={['user', 'agency', 'admin']}>
              <MesBesoins />
            </PrivateRoute>
          } />
          <Route path="/admin" element={
            <PrivateRoute roles={['admin']}>
              <AdminDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/besoins" element={
            <PrivateRoute roles={['admin']}>
              <AdminBesoins />
            </PrivateRoute>
          } />
          <Route path="/admin/whatsapp" element={
            <PrivateRoute roles={['admin']}>
              <AdminWhatsApp />
            </PrivateRoute>
          } />
          <Route path="/tarifs" element={<Pricing />} />
          <Route path="/paiement/:planId" element={
            <PrivateRoute roles={['agency']}>
              <PaymentPage />
            </PrivateRoute>
          } />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/choisir-compte-google" element={<GoogleAccountChoice />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe/:token" element={<ResetPassword />} />
          <Route path="/verifier-email/:token" element={<VerifyEmail />} />
          <Route path="/agence/:id" element={<AgencePage />} />
          <Route path="/profil/:id" element={<ProfilPage />} />
          <Route path="/confidentialite" element={<PrivacyPolicy />} />
          <Route path="/conditions" element={<TermsOfUse />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <InstallPrompt />
      <WhatsAppGroupButton />
      <Footer />
    </BrowserRouter>
  );
}
